<template>
    <Teleport to="body" :disabled="isServer">
        <div v-if="show" ref="tooltipRef"
            class="fixed bg-gray-800 opacity-80 border border-white rounded-xl text-white text-sm p-2 shadow-lg z-50"
            :style="tooltipStyle">
            <slot />
        </div>
    </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { calculateOutlineTooltipLeft } from './outlineTooltipPosition.js';

const isServer = typeof window === 'undefined';

const props = defineProps({
    show: { type: Boolean, default: true },
    position: { type: Object, required: true },
    anchor: { type: Object, default: null },
    maxWidth: { type: String, default: '400px' },
});

const tooltipRef = ref(null);
const tooltipStyle = ref({
    top: `${props.position.y}px`,
    left: `${props.position.x}px`,
    maxWidth: props.maxWidth,
});
let resizeObserver = null;

function viewportWidth() {
    return document.documentElement.clientWidth || window.innerWidth;
}

function updatePosition() {
    if (isServer || !props.show || !tooltipRef.value) return;

    const tooltipWidth = tooltipRef.value.getBoundingClientRect().width;
    const width = viewportWidth();
    const left = Number(props.anchor?.left ?? props.position.x);
    const right = Number(props.anchor?.right ?? left);
    const xPosition = calculateOutlineTooltipLeft({
        anchorLeft: left,
        anchorRight: right,
        tooltipWidth,
        viewportWidth: width,
    });

    tooltipStyle.value = {
        top: `${props.position.y}px`,
        left: `${xPosition}px`,
        maxWidth: props.maxWidth,
    };
}

function schedulePositionUpdate() {
    nextTick(updatePosition);
}

watch(
    () => [
        props.show,
        props.position.x,
        props.position.y,
        props.anchor?.left,
        props.anchor?.right,
        props.maxWidth,
    ],
    schedulePositionUpdate,
    { immediate: true },
);

onMounted(() => {
    resizeObserver = typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(schedulePositionUpdate);
    if (resizeObserver && tooltipRef.value) resizeObserver.observe(tooltipRef.value);
    window.addEventListener('resize', schedulePositionUpdate);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    window.removeEventListener('resize', schedulePositionUpdate);
});

</script>

